// ─── SPEC-LOCKED CONTENT ───────────────────────────────────────────────────
// Source of truth for Phases 1, 2 & 3.

export const IDENTITY = {
  name: "Mekhal Raj Rajendran",
  shortName: "Mekhal",
  title: "Data Systems Architect · AI Founder · Builder",
  tagline: "I build systems that turn data into decisions — and startups that prove it.",
  location: "Boston, MA",
  email: "mekhalrajr@gmail.com",
  linkedin: "https://linkedin.com/in/mekhalrajr",
  github: "https://github.com/mekhalrajr",
  tableau: "#",
};

export const TICKER_STATS = [
  "$2M+ transactions resolved",
  "200+ datasets integrated",
  "800GB/day ETL pipelines",
  "40K customer migration",
  "80hrs/month reporting eliminated",
  "6+ years experience",
  "6 products shipped",
  "3 degrees earned",
  "100+ associates trained",
  "∞ curiosity",
];

export const CAREER_CHART_DATA = [
  { year: "2018", label: "BS Physics", value: 10, event: "Graduated Mahatma Gandhi University. Physics shaped systems thinking & first-principles reasoning." },
  { year: "2018", label: "TCS Associate", value: 20, event: "Joined TCS at Western Union. First exposure to AML compliance & enterprise-scale data." },
  { year: "2019", label: "Programmer", value: 32, event: "Promoted to Programmer. Automated alert monitoring, improved customer satisfaction index by 30%." },
  { year: "2020", label: "Asst. Systems Eng.", value: 44, event: "Promoted to ASE. Built real-time health monitoring. Resolved $2M+ in critical transactions." },
  { year: "2021", label: "MS Analytics", value: 52, event: "Dean's Scholar at Boston University Questrom. Merged math, business & technology." },
  { year: "2022", label: "Citizens Bank", value: 63, event: "Joined Citizens Bank. Built data lake integrating 200+ datasets. 800GB/day ETL pipelines." },
  { year: "2023", label: "Senior Analyst", value: 74, event: "Led 40K-customer Visa→Mastercard migration. Eliminated 80hrs/month manual reporting." },
  { year: "2024", label: "Forte Consulting", value: 80, event: "Joined Forte as Systems Analyst. SQL optimization, data modernization, AI experimentation." },
  { year: "2025", label: "Finide Founded", value: 92, event: "Co-founded Finide. AI-powered financial forecasting. Accepted to Founder University & LAUNCH." },
  { year: "2026", label: "Now", value: 100, event: "Building Finide + Memories + SMART. 6 products shipped. Pursuing Executive MS in AI at New England College." },
];

export const ORIGIN_STORY = [
  {
    id: "physics",
    chapter: "01",
    title: "The Physicist",
    years: "2013 – 2018",
    color: "#1a1a1a",
    location: "Kerala, India",
    items: [
      "BS Physics, Mahatma Gandhi University",
      "Restored a windmill to working condition",
      "Built & tested electrical circuits",
      "NCC Cadet · NSS Volunteer · SKY UP Events",
      "Learned: systems have rules — find them.",
    ],
  },
  {
    id: "tcs",
    chapter: "02",
    title: "The Analyst",
    years: "2018 – 2021",
    color: "#2a2a2a",
    location: "Denver, CO",
    items: [
      "TCS / Western Union — Associate → Programmer → ASE",
      "AML compliance & transaction monitoring",
      "Investigated 100+ cases weekly",
      "Reduced server downtime 20% via Splunk monitoring",
      "Resolved $2M+ in critical production issues",
      "Star Team · Special Initiative · Special Achievement Awards",
    ],
  },
  {
    id: "bu",
    chapter: "03",
    title: "The Scholar",
    years: "2021 – 2022",
    color: "#1a1a1a",
    location: "Boston, MA",
    items: [
      "MS Business Analytics, Boston University Questrom",
      "Dean's Achievement Scholar",
      "MATLAB capstone: Money Market Funds",
      "Healthcare analytics & value-based care research",
      "Social Chair, GSO — $50K budget, 18K+ members",
      "Merged: Math + Business + Technology",
    ],
  },
  {
    id: "citizens",
    chapter: "04",
    title: "The Architect",
    years: "2022 – 2024",
    color: "#2a2a2a",
    location: "Westwood, MA",
    items: [
      "Data Analyst, Citizens Bank",
      "Data Lake: 200+ datasets, AWS Redshift, Informatica, Collibra",
      "ETL pipelines: 800 GB/day consumer banking data",
      "40K-customer Visa → Mastercard migration",
      "Eliminated 80+ hrs/month manual reporting",
      "MDM: 30% reduction in data duplication",
    ],
  },
  {
    id: "forte",
    chapter: "05",
    title: "The Consultant",
    years: "Feb 2025 – Present",
    color: "#1a1a1a",
    location: "Princeton, NJ (Remote)",
    items: [
      "Systems Analyst, Forte Consulting",
      "40% query runtime reduction",
      "20+ customer table data model design",
      "End-to-end data modernization across 5+ systems",
      "35% manual effort eliminated via automation",
      "AI tool experimentation: OpenAI, LLM workflows",
    ],
  },
  {
    id: "finide",
    chapter: "06",
    title: "The Founder",
    years: "Oct 2025 – Present",
    color: "#e8c547",
    location: "Boston, MA",
    items: [
      "Co-Founder & CTO, Finide",
      "AI-powered financial forecasting platform",
      "Production MVP in 4 months",
      "70% reduction in time-to-insight",
      "Accepted: Founder University & LAUNCH",
      "6 products shipped in 6 months",
      "Executive MS in AI, New England College (In Progress)",
    ],
  },
];

export const EXPERIENCE = [
  {
    company: "Forte Consulting",
    role: "Systems Analyst",
    period: "Feb 2025 – Present",
    location: "Princeton, NJ (Remote)",
    bullets: [
      "Designed relational data models across 20+ customer tables, improving analytics query performance and enabling self-service reporting",
      "Led end-to-end customer data modernization project across 5+ systems, translating stakeholder requirements into production-ready specs",
      "Optimized complex SQL queries reducing runtime by 40% and improving reporting SLAs",
      "Analyzed system workflows processing 100K+ daily records, eliminating 35% of manual operational effort",
      "Partnered with 10+ cross-functional stakeholders to align business logic, data definitions, and governance standards",
    ],
  },
  {
    company: "Finide",
    role: "Co-Founder",
    period: "Oct 2025 – Present",
    location: "Boston, MA",
    tag: "startup",
    bullets: [
      "Built AI-powered financial intelligence platform 0→1, defining product analytics framework and success metrics",
      "Designed end-to-end data architecture processing unstructured financial documents via LLM extraction (Claude, Groq, Llama)",
      "Implemented conversational AI analytics interface reducing time-to-insight by 70% with 95%+ validation accuracy",
      "Delivered production-ready MVP in 4 months while working full-time at Forte",
      "Instrumented multi-tenant product analytics (Supabase/Postgres): activation, retention, forecast accuracy, feature adoption",
    ],
  },
  {
    company: "Citizens Bank",
    role: "Data Analyst",
    period: "Jul 2022 – Oct 2024",
    location: "Westwood, MA",
    bullets: [
      "Led analytics strategy for 40,000-customer portfolio migration, coordinating FICO, Visa, Mastercard and executive stakeholders",
      "Automated marketing analytics pipeline: eliminated 80+ hours/month manual reporting, enabled real-time campaign tracking",
      "Developed and maintained scalable Data Lake integrating 200+ datasets via AWS Redshift, Informatica, and Collibra",
      "Designed ETL pipelines using AWS and Alteryx processing 800 GB/day of consumer banking data",
      "Owned MDM strategy reducing data duplication 30% across 200+ customer/product datasets",
    ],
  },
  {
    company: "TCS / Western Union",
    location: "Denver, CO",
    roles: [
      {
        role: "Assistant Systems Engineer",
        period: "Nov 2020 – Jun 2021",
        bullets: [
          "Analyzed transactional data using SQL and IBM DB2 to identify AML risk patterns and strengthen compliance",
          "Built real-time system health monitoring using Splunk, ServiceNow & Shell scripting — reduced downtime 20%",
          "Managed cross-functional compliance team, reducing manual case review time 15% — Star Team Award",
          "Resolved critical production issues affecting $2M+ in transactions — Special Initiative Award",
        ],
      },
      {
        role: "Programmer",
        period: "Nov 2019 – Nov 2020",
        bullets: [
          "Improved Customer Satisfaction Index by 30% through resolution of 50% of Money Transfer portfolio incidents",
          "Led training sessions for 100+ associates as Talent Development Lead — Special Achievement Award",
          "Automated alert monitoring integrations using Shell scripting and Dynatrace, improving critical response time 10%",
        ],
      },
      {
        role: "Associate",
        period: "Nov 2018 – Nov 2019",
        bullets: [
          "Served as Team Lead for 30+ associates as part of TCS's Initial Learning Program",
          "Investigated 100+ AML cases weekly, reducing false positives 25% while maintaining KYC compliance",
          "Supported production release cycles and UAT validation across enterprise payment systems",
        ],
      },
    ],
  },
];

// Other experience — shown in a sub-section below main experience
export const OTHER_EXPERIENCE = [
  {
    company: "Questrom School of Business, Boston University",
    role: "Graduate Assistant",
    period: "Sep 2021 – Apr 2022",
    location: "Boston, MA",
    tag: "part-time",
    bullets: [
      "Graduate Assistant for the Online MBA program",
    ],
  },
  {
    company: "Cactus Communications",
    role: "Freelance Editor",
    period: "Mar 2020 – Jul 2020",
    location: "Mumbai, India",
    tag: "part-time",
    bullets: [
      "Reviewed and edited scientific journals and research papers on various topics under Physics",
    ],
  },
];

// ─── PHASE 2 DATA ──────────────────────────────────────────────────────────

export const PROJECTS = [
  {
    id: "finide",
    name: "Finide",
    tag: "Startup · Co-Founder",
    status: "Live — Active Development",
    statusDot: "green",
    period: "Oct 2025 – Present",
    tagline: "AI-powered financial forecasting for businesses.",
    description: "Turns unstructured financial documents into structured datasets for real-time cash flow prediction and AI-driven forecasting. Built with Claude, Groq, Llama, Supabase/Postgres.",
    stack: ["Claude API", "Groq", "Llama", "Supabase", "Postgres", "React", "Python"],
    url: "https://finide.ai",
    metrics: [
      { label: "Time-to-insight", value: "70% ↓" },
      { label: "Validation accuracy", value: "95%+" },
      { label: "MVP delivery", value: "4 months" },
      { label: "Acceptance", value: "Founder U + LAUNCH" },
    ],
    deck: [
      { label: "Problem", text: "Businesses make financial decisions blind — no real-time forecasting, manual spreadsheets, no AI layer." },
      { label: "Solution", text: "Finide ingests financial documents, extracts structured data via LLMs, and generates forward-looking cash flow models." },
      { label: "TAM", text: "$16.5B financial analytics market. Target: SMBs and startups spending 10+ hours/week on manual forecasting." },
      { label: "Traction", text: "Production MVP live. Accepted to Founder University & LAUNCH accelerator programs." },
      { label: "Stack", text: "Claude + Groq + Llama for extraction. Supabase/Postgres for multi-tenant data. React frontend." },
      { label: "Ask", text: "Pre-seed $500K to expand go-to-market, hire first engineer, and reach 50 paying customers." },
    ],
  },
  {
    id: "memories",
    name: "Memories",
    tag: "Startup · Founder",
    status: "In Development — Launching 2026",
    statusDot: "amber",
    period: "2025 – Present",
    tagline: "Voice-first personal relationship intelligence.",
    description: "Records conversations, transcribes via Deepgram, and automatically creates intelligent profiles for people you meet. Built in React Native with Groq for real-time structured data extraction.",
    stack: ["React Native", "Deepgram", "Groq", "Supabase", "Claude API", "Expo"],
    url: null,
    metrics: [
      { label: "Platform", value: "React Native" },
      { label: "AI Layer", value: "Deepgram + Groq" },
      { label: "Launch", value: "2026" },
      { label: "Focus", value: "Behavioral Design" },
    ],
  },
  {
    id: "carehelp",
    name: "CareHelp",
    tag: "Startup · Builder",
    status: "In Development",
    statusDot: "amber",
    period: "2025 – Present",
    tagline: "Medication workflow SaaS for UK care homes.",
    description: "Regulatory-compliant PWA for care home medication management. UK data hosting, compliance-first architecture, AI-generated handover summaries, designed for non-technical staff.",
    stack: ["Next.js", "PWA", "Supabase", "UK Infrastructure", "React", "Claude API"],
    url: "https://carehelp-zeta.vercel.app",
    metrics: [
      { label: "Market", value: "UK Care Homes" },
      { label: "Architecture", value: "PWA" },
      { label: "Compliance", value: "UK GDPR" },
      { label: "Stage", value: "MVP Build" },
    ],
  },
  {
    id: "smart",
    name: "SMART",
    tag: "Product · Builder",
    status: "Shipped",
    statusDot: "green",
    period: "2025 – Present",
    tagline: "AI-powered subscription management platform.",
    description: "Conversational AI assistant for managing subscriptions. Multi-API integrations, usage analytics, and intelligent spend recommendations. Built on Next.js 14, Supabase, and Vercel.",
    stack: ["Next.js 14", "Supabase", "Vercel", "Claude API", "TypeScript"],
    url: "https://smartsubscriptions.vercel.app",
    metrics: [
      { label: "Stack", value: "Next.js 14" },
      { label: "AI Layer", value: "Conversational" },
      { label: "Infra", value: "Vercel + Supabase" },
      { label: "Status", value: "Shipped" },
    ],
  },
  {
    id: "boston-neighborhood",
    name: "Boston Neighborhood Intelligence",
    tag: "Civic Tech · Builder",
    status: "Shipped",
    statusDot: "green",
    period: "2025",
    tagline: "Civic data dashboard for 23 Boston neighborhoods.",
    description: "Geospatial data pipeline processing 700K+ records across 23 Boston neighborhoods. Python/geopandas pipeline, newspaper editorial design aesthetic, built for civic transparency.",
    stack: ["Python", "geopandas", "React", "Recharts", "Vercel"],
    url: "https://mekhalraj.github.io/boston-neighborhood-intelligence/",
    metrics: [
      { label: "Neighborhoods", value: "23" },
      { label: "Records", value: "700K+" },
      { label: "Pipeline", value: "geopandas" },
      { label: "Design", value: "Editorial" },
    ],
  },
  {
    id: "snow-maps",
    name: "Boston + NYC Snow Maps",
    tag: "Geo · Builder",
    status: "Shipped",
    statusDot: "green",
    period: "2025",
    tagline: "Real-time geospatial snow visualization for two cities.",
    description: "Live snow accumulation visualization tools for Boston and New York City. Geospatial rendering with real-time weather data integration, built for public use during storm events.",
    stack: ["React", "Geospatial APIs", "Weather APIs", "Vercel"],
    url: "https://mekhalraj.github.io/boston-snow-map",
    metrics: [
      { label: "Cities", value: "2" },
      { label: "Data", value: "Real-time" },
      { label: "Type", value: "Geospatial" },
      { label: "Status", value: "Live" },
    ],
  },
  {
    id: "neura",
    name: "Neura",
    tag: "Advisory",
    status: "Past Advisory",
    statusDot: "grey",
    period: "2025",
    tagline: "Product strategy & GTM advisory.",
    description: "Advised on product feature refinement, roadmap prioritization, and go-to-market positioning. Brought data-informed product thinking to early-stage decisions.",
    stack: ["Product Strategy", "GTM", "Roadmap", "Analytics"],
    url: null,
    metrics: [
      { label: "Role", value: "Advisor" },
      { label: "Focus", value: "Product + GTM" },
    ],
  },
];

export const SKILLS = {
  "Data Engineering": ["SQL", "ETL Pipelines", "AWS Redshift", "dbt", "Snowflake", "Alteryx", "Informatica", "Collibra", "Data Modeling", "MDM", "Data Governance", "Stitch"],
  "Analytics & BI": ["Tableau", "Power BI", "Looker", "Excel (Advanced)", "A/B Testing", "Regression Modeling", "Statistical Analysis"],
  "AI & Machine Learning": ["Claude API", "OpenAI", "Groq", "Llama", "Gemini", "LLM Integration", "Prompt Engineering", "RAG Systems", "Deepgram", "scikit-learn", "TensorFlow"],
  "Cloud & Infrastructure": ["AWS (Redshift, Athena)", "GCP (BigQuery)", "Supabase", "Vercel", "GitHub", "Postgres", "Salesforce", "Oracle DB", "Oracle ERP", "ServiceNow"],
  "Programming": ["Python (pandas, scikit-learn)", "SQL", "R", "JavaScript", "React", "React Native", "Next.js", "TypeScript", "Java", "Shell Scripting", "Linux"],
  "Product & Strategy": ["Product Analytics", "Funnel Analysis", "Cohort Segmentation", "User Research", "Roadmap Planning", "GTM Strategy", "Figma", "Canva", "Jira", "Confluence"],
};

export const EDUCATION = [
  {
    degree: "Executive MS in Artificial Intelligence",
    school: "New England College",
    location: "Henniker, NH",
    period: "In Progress",
    highlight: "Focus: ML, LLM architectures, Applied AI strategy",
  },
  {
    degree: "MS Business Analytics (STEM)",
    school: "Boston University — Questrom School of Business",
    location: "Boston, MA",
    period: "May 2022",
    highlight: "Dean's Achievement Scholar · MATLAB capstone: Money Market Funds",
  },
  {
    degree: "BS Physics",
    school: "Mahatma Gandhi University",
    location: "Kerala, India",
    period: "May 2018",
    highlight: "Restored a windmill · Built & tested electrical circuits · First-principles thinking",
  },
];

export const READING_LIST = [
  { type: "Reading", title: "The Lean Startup", author: "Eric Ries", note: "Back to basics on validated learning." },
  { type: "Thinking", title: "AI unit economics", note: "Most teams ship AI features without modeling cost per inference. That's a ticking clock." },
  { type: "Reading", title: "Designing Data-Intensive Applications", author: "Martin Kleppmann", note: "Still the best book on data systems." },
  { type: "Thinking", title: "Behavioral loops in consumer apps", note: "Duolingo's streak mechanic isn't about learning — it's about identity. That's the unlock." },
  { type: "Listening", title: "Acquired Podcast", note: "The Nvidia episode reshaped how I think about platform bets." },
  { type: "Thinking", title: "The governance gap in AI", note: "Everyone's racing to ship. Nobody's building the audit trail. That's the next category." },
];

export const WHAT_IM_BUILDING = [
  {
    name: "Finide",
    description: "AI financial forecasting",
    metrics: [
      { label: "Status", value: "Live" },
      { label: "MVP", value: "Shipped" },
      { label: "Accuracy", value: "95%+" },
    ],
    color: "#e8c547",
  },
  {
    name: "Memories",
    description: "Voice-first relationship intelligence",
    metrics: [
      { label: "Status", value: "Building" },
      { label: "Platform", value: "React Native" },
      { label: "Launch", value: "2026" },
    ],
    color: "#c084fc",
  },
  {
    name: "CareHelp",
    description: "Medication workflow SaaS for UK care homes",
    metrics: [
      { label: "Status", value: "MVP Build" },
      { label: "Market", value: "UK Care" },
      { label: "Stack", value: "Next.js PWA" },
    ],
    color: "#67e8f9",
  },
  {
    name: "SMART",
    description: "AI subscription management platform",
    metrics: [
      { label: "Status", value: "Shipped" },
      { label: "Stack", value: "Next.js 14" },
      { label: "AI", value: "Conversational" },
    ],
    color: "#86efac",
  },
];

// ─── PHASE 3 DATA ──────────────────────────────────────────────────────────

export const LEADERSHIP = [
  { role: "Volunteer Counselor", org: "Wellmet Inc", period: "Current", note: "Mental health and community support" },
  { role: "Team Leader", org: "9/11 Day Boston", period: "Sep 2025", note: "Led meal packing initiative" },
  { role: "Brand Ambassador", org: "Citizens Bank", period: "2022 – 2024", note: "Public-facing brand representation" },
  { role: "Social Chair", org: "Graduate Students Organization, Boston University", period: "2021 – 2022", note: "$50K event budget · 18,000+ member community" },
  { role: "Admissions Ambassador", org: "Questrom MSBA Program", period: "2021 – Present", note: "Prospective student outreach and mentorship" },
  { role: "Student Volunteer", org: "National Service Scheme", period: "2015 – 2017", note: "Community service and social development" },
  { role: "Cadet", org: "National Cadet Corps, India", period: "2013 – 2015", note: "Military-style discipline and leadership training" },
  { role: "Member", org: "SKY UP Event Management Team, St. Aloysius College", period: "2016 – 2017", note: "Coordinated 10+ college events" },
];

export const PERSONAL_LAYER = [
  { emoji: "\ud83c\udfa3", label: "Fishing", description: "Avid fisherman across Massachusetts and New England. Best thinking happens on the water." },
  { emoji: "\ud83c\udfce\ufe0f", label: "Cars & Racing", description: "BMW enthusiast. Loves go-karting, racing, and never misses an F1 weekend." },
  { emoji: "\ud83c\udf73", label: "Cooking", description: "Loves experimenting with new recipes. Cooking is just chemistry with better output." },
  { emoji: "\ud83d\udd27", label: "Building & Tinkering", description: "Debugging, deployment, startup tooling, side projects — if it can be built, it will be." },
];

export const THINGS_I_BELIEVE = [
  {
    quote: "The best data infrastructure is the kind nobody notices — until it's gone.",
    context: "On building invisible, mission-critical systems",
  },
  {
    quote: "Compliance isn't a constraint. It's the architecture.",
    context: "On working in regulated industries",
  },
  {
    quote: "You can't govern what you haven't defined. Most data problems are vocabulary problems.",
    context: "On data governance and clarity",
  },
];

export const ANTI_RESUME = {
  learning: "I'm actively learning mobile development — iOS and Android — which is pushing me to think about product differently than I do as a data engineer. I'm also going deep on leveraging AI as a coding collaborator, not just a tool. Frontend and UI design are newer muscles for me — I've always been strong on the systems and data side, so learning to think visually and ship things people actually interact with is genuinely exciting. Basically I'm closing the gap between what I can architect and what I can build end-to-end myself.",
  drains: "Work that moves slowly not because of complexity, but because of process — meetings that could be emails, approval chains for decisions that should take twenty minutes. I do my best work when there's a clear problem, real stakes, and autonomy to go solve it. I get restless when I'm just maintaining something that isn't growing.",
  doDifferently: "I'd have shipped more, sooner, and been less precious about it. The tools available now — AI coding assistants, no-code infra, open-source models — would have let me build things in weeks that used to take teams and funding. I'd have started building products much sooner instead of waiting until I felt 'ready.' Now I build first and refine fast. I wish I'd found that instinct earlier.",
};

export const MANIFESTO = {
  title: "If I Were Your Hire",
  paragraphs: [
    "I won't just fill a seat. I'll find the thing nobody's measuring and build a system around it.",
    "I come from compliance environments where a bad query costs millions. That makes me careful. I come from startups where a slow decision costs everything. That makes me fast. The tension between those two things is where I live.",
    "You'll get someone who can sit in a strategy meeting and a terminal in the same afternoon. Who writes documentation before it's asked for. Who notices when a dashboard is technically correct but strategically useless.",
    "I'm also building multiple products simultaneously. That's not a distraction — it's proof that I think like an owner. Every system I build at work, I'd be comfortable owning in production at 2am. Because I've done that.",
    "If you need someone who follows playbooks, I'm probably not your person. If you need someone who writes them — let's talk.",
  ],
};

// Terminal commands for easter egg
export const TERMINAL_COMMANDS = {
  help: "Available commands: about, skills, experience, projects, contact, startups, education, beliefs, clear, exit",
  about: "Mekhal Raj Rajendran — Data Systems Architect · AI Founder · Builder. Based in Boston, MA. 6+ years building data systems, 6 products shipped, now founding AI startups.",
  skills: "SQL · Python · AWS Redshift · dbt · Snowflake · Tableau · Claude API · Groq · Llama · Gemini · React · React Native · Next.js · TypeScript · Supabase · Postgres · Figma · Linux",
  experience: "Forte Consulting (2025–Present) → Finide Co-Founder (2025–Present) → Citizens Bank (2022–2024) → TCS/Western Union (2018–2021)",
  projects: "Finide: AI financial forecasting (Live) | Memories: Voice-first relationship intelligence (Building) | CareHelp: UK medication SaaS (MVP) | SMART: AI subscription management (Shipped) | Boston Neighborhood Intelligence: civic data dashboard (Shipped) | Boston + NYC Snow Maps: geospatial viz (Live)",
  contact: "Email: mekhalrajr@gmail.com | LinkedIn: linkedin.com/in/mekhalrajr | GitHub: github.com/mekhalrajr",
  startups: "Finide → AI-powered financial forecasting, accepted to Founder University & LAUNCH. Memories → Voice-first personal CRM, React Native, launching 2026. CareHelp → Medication SaaS for UK care homes. SMART → AI subscription management platform.",
  education: "Executive MS AI (New England College, In Progress) | MS Business Analytics (BU Questrom, 2022) | BS Physics (MG University, 2018)",
  beliefs: '"The best data infrastructure is the kind nobody notices — until it\'s gone." | "Compliance isn\'t a constraint. It\'s the architecture." | "You can\'t govern what you haven\'t defined."',
};

// SQL Easter egg — queryable data
export const SQL_TABLES = {
  experience: [
    { company: "Forte Consulting", role: "Systems Analyst", start_year: 2025, end_year: null, location: "Princeton, NJ" },
    { company: "Finide", role: "Co-Founder", start_year: 2025, end_year: null, location: "Boston, MA" },
    { company: "Citizens Bank", role: "Data Analyst", start_year: 2022, end_year: 2024, location: "Westwood, MA" },
    { company: "TCS / Western Union", role: "Assistant Systems Engineer", start_year: 2020, end_year: 2021, location: "Denver, CO" },
    { company: "TCS / Western Union", role: "Programmer", start_year: 2019, end_year: 2020, location: "Denver, CO" },
    { company: "TCS / Western Union", role: "Associate", start_year: 2018, end_year: 2019, location: "Denver, CO" },
  ],
  skills: [
    { name: "SQL", category: "Data Engineering", proficiency: "Expert" },
    { name: "Python", category: "Programming", proficiency: "Expert" },
    { name: "AWS Redshift", category: "Cloud & Infrastructure", proficiency: "Advanced" },
    { name: "Claude API", category: "AI & Machine Learning", proficiency: "Advanced" },
    { name: "React", category: "Programming", proficiency: "Advanced" },
    { name: "Next.js", category: "Programming", proficiency: "Advanced" },
    { name: "TypeScript", category: "Programming", proficiency: "Intermediate" },
    { name: "React Native", category: "Programming", proficiency: "Intermediate" },
    { name: "Gemini", category: "AI & Machine Learning", proficiency: "Intermediate" },
    { name: "Groq", category: "AI & Machine Learning", proficiency: "Advanced" },
    { name: "Tableau", category: "Analytics & BI", proficiency: "Expert" },
    { name: "dbt", category: "Data Engineering", proficiency: "Advanced" },
    { name: "Supabase", category: "Cloud & Infrastructure", proficiency: "Advanced" },
    { name: "Figma", category: "Product & Strategy", proficiency: "Intermediate" },
    { name: "Linux", category: "Programming", proficiency: "Intermediate" },
  ],
  projects: [
    { name: "Finide", status: "Live", type: "Startup", year: 2025 },
    { name: "Memories", status: "Building", type: "Startup", year: 2025 },
    { name: "CareHelp", status: "MVP", type: "Startup", year: 2025 },
    { name: "SMART", status: "Shipped", type: "Product", year: 2025 },
    { name: "Boston Neighborhood Intelligence", status: "Shipped", type: "Civic Tech", year: 2025 },
    { name: "Boston Snow Map", status: "Live", type: "Geo", year: 2025 },
    { name: "NYC Snow Map", status: "Live", type: "Geo", year: 2025 },
  ],
};

// ─── AI CHAT SYSTEM PROMPT (spec item L) ───────────────────────────────────
export const AI_CHAT_SYSTEM_PROMPT = `You are Mekhal Raj Rajendran's portfolio AI assistant. You respond as Mekhal, in first person, warmly and directly — no corporate speak.

Background:
- Systems Analyst at Forte Consulting (Feb 2025–present), Princeton NJ remote
- Co-Founder & CTO of Finide, an AI-powered financial forecasting startup (Oct 2025–present). Accepted to Founder University & LAUNCH.
- Previously Data Analyst at Citizens Bank (2022–2024): built data lake with 200+ datasets, 800GB/day ETL, led 40K-customer migration
- Before that: TCS/Western Union (2018–2021): AML compliance, production systems, $2M+ transactions resolved
- MS Business Analytics (Boston University, Dean's Scholar), Executive MS AI (New England College, in progress), BS Physics
- Also building: Memories app (voice-first relationship intelligence, React Native + Deepgram + Groq), CareHelp (UK medication SaaS, Next.js PWA), SMART (AI subscription management platform, Next.js 14 + Supabase)
- Shipped 6 products in ~6 months while consulting full-time
- Civic tech: Boston Neighborhood Intelligence (23 neighborhoods, 700K+ records, Python/geopandas) and Boston + NYC Snow Maps (geospatial viz tools)
- Volunteer Counselor at Wellmet Inc. Avid fisherman in New England. BMW enthusiast. Loves cooking and go-karting.

Always emphasize:
- Open to roles across the US — hybrid, remote, or in-person
- Deeply interested in fintech and AI infrastructure
- Building multiple products in parallel — that's a feature, not a bug (shows ownership mindset)
- Open to senior IC and leadership roles equally

Never discuss: specific salary expectations, visa/work authorization, anything negative about past employers.

Keep responses concise (2-4 sentences). Be warm, specific, and confident. If asked something you don't know, say so honestly.`;
